import Head from 'next/head';
import React from 'react';

import { ILocalityPriceAggregations } from '@/services/getLocalityPriceAggregation';
import { ILocalityRentalReview } from '@/services/getLocalityRentalReviews';
import { ILocalityRentalReviewsAggregations } from '@/services/getLocalityRentalReviewsAggregations';

type TReviewsStructuredDataProps = {
  url: string;
  urlPath: string;
  reviews: ILocalityRentalReview[];
  reviewsAggregations?: ILocalityRentalReviewsAggregations;
  priceAggregation?: ILocalityPriceAggregations;
  localityName: string;
};

enum ECohortType {
  LowCountCohort = 'Low Count Cohort',
  HighCountCohort = 'High Count Cohort',
  PricingCohort = 'Pricing Cohort',
}

type TLowCountCohort = {
  type: ECohortType.LowCountCohort;
  num_reviews: number;
  avg_score: number;
};

type THighCountCohort = {
  type: ECohortType.HighCountCohort;
  num_reviews: number;
  avg_score: number;
};

type TPricingCohort = {
  type: ECohortType.PricingCohort;
  num_rentals: number;
  min_nightly_rate: number;
  max_nightly_rate: number;
};

const localitiesData: Record<string, TLowCountCohort | THighCountCohort | TPricingCohort> = {
  // Low Count Cohort
  '/rv-rental/north-dakota/bismarck': {
    type: ECohortType.LowCountCohort,
    num_reviews: 24,
    avg_score: 4.958333333,
  },
  '/rv-rental/louisiana/lafayette': {
    type: ECohortType.LowCountCohort,
    num_reviews: 90,
    avg_score: 4.122222222,
  },
  '/rv-rental/illinois/champaign': {
    type: ECohortType.LowCountCohort,
    num_reviews: 156,
    avg_score: 4.711538462,
  },
  '/rv-rental/florida/key-west': {
    type: ECohortType.LowCountCohort,
    num_reviews: 27,
    avg_score: 4.888888889,
  },
  '/rv-rental/california/pinecrest': {
    type: ECohortType.LowCountCohort,
    num_reviews: 135,
    avg_score: 4.807407407,
  },
  '/rv-rental/nebraska/kearney': {
    type: ECohortType.LowCountCohort,
    num_reviews: 6,
    avg_score: 4.166666667,
  },
  '/rv-rental/arizona/yuma': {
    type: ECohortType.LowCountCohort,
    num_reviews: 52,
    avg_score: 3.653846154,
  },
  '/rv-rental/nevada/elko': {
    type: ECohortType.LowCountCohort,
    num_reviews: 5,
    avg_score: 5,
  },
  '/rv-rental/tennessee/kingsport': {
    type: ECohortType.LowCountCohort,
    num_reviews: 176,
    avg_score: 4.772727273,
  },
  '/rv-rental/oregon/roseburg': {
    type: ECohortType.LowCountCohort,
    num_reviews: 124,
    avg_score: 4.47983871,
  },

  // High Count Cohort
  '/rv-rental/minnesota/minneapolis': {
    type: ECohortType.HighCountCohort,
    num_reviews: 2284,
    avg_score: 4.763791594,
  },
  '/rv-rental/texas/houston': {
    type: ECohortType.HighCountCohort,
    num_reviews: 2317,
    avg_score: 4.521795425,
  },
  '/rv-rental/illinois/chicago': {
    type: ECohortType.HighCountCohort,
    num_reviews: 1671,
    avg_score: 4.698384201,
  },
  '/rv-rental/florida/orlando': {
    type: ECohortType.HighCountCohort,
    num_reviews: 2455,
    avg_score: 4.575356415,
  },
  '/rv-rental/california/san-francisco': {
    type: ECohortType.HighCountCohort,
    num_reviews: 4822,
    avg_score: 4.67959353,
  },
  '/rv-rental/georgia/atlanta': {
    type: ECohortType.HighCountCohort,
    num_reviews: 2101,
    avg_score: 4.550690148,
  },
  '/rv-rental/washington/seattle': {
    type: ECohortType.HighCountCohort,
    num_reviews: 4544,
    avg_score: 4.713578345,
  },
  '/rv-rental/nevada/las-vegas': {
    type: ECohortType.HighCountCohort,
    num_reviews: 2991,
    avg_score: 4.625543297,
  },
  '/rv-rental/tennessee/nashville': {
    type: ECohortType.HighCountCohort,
    num_reviews: 1123,
    avg_score: 4.649154052,
  },
  '/rv-rental/oregon/portland': {
    type: ECohortType.HighCountCohort,
    num_reviews: 4169,
    avg_score: 4.737826817,
  },

  // Pricing Cohort
  '/rv-rental/wisconsin/milwaukee': {
    type: ECohortType.PricingCohort,
    num_rentals: 770,
    min_nightly_rate: 1,
    max_nightly_rate: 1200,
  },
  '/rv-rental/texas/austin': {
    type: ECohortType.PricingCohort,
    num_rentals: 822,
    min_nightly_rate: 49,
    max_nightly_rate: 750,
  },
  '/rv-rental/missouri/st-louis': {
    type: ECohortType.PricingCohort,
    num_rentals: 183,
    min_nightly_rate: 70,
    max_nightly_rate: 800,
  },
  '/rv-rental/florida/tampa': {
    type: ECohortType.PricingCohort,
    num_rentals: 836,
    min_nightly_rate: 10,
    max_nightly_rate: 1275,
  },
  '/rv-rental/california/san-diego': {
    type: ECohortType.PricingCohort,
    num_rentals: 1084,
    min_nightly_rate: 1,
    max_nightly_rate: 2500,
  },
  '/rv-rental/south-carolina/myrtle-beach': {
    type: ECohortType.PricingCohort,
    num_rentals: 194,
    min_nightly_rate: 68,
    max_nightly_rate: 1500,
  },
  '/rv-rental/washington-spokane': {
    type: ECohortType.PricingCohort,
    num_rentals: 222,
    min_nightly_rate: 10,
    max_nightly_rate: 550,
  },
  '/rv-rental/utah/salt-lake-city': {
    type: ECohortType.PricingCohort,
    num_rentals: 1115,
    min_nightly_rate: 2.1,
    max_nightly_rate: 1795,
  },
  '/rv-rental/virginia/richmond': {
    type: ECohortType.PricingCohort,
    num_rentals: 127,
    min_nightly_rate: 55,
    max_nightly_rate: 399,
  },
  '/rv-rental/montana/bozeman': {
    type: ECohortType.PricingCohort,
    num_rentals: 193,
    min_nightly_rate: 50,
    max_nightly_rate: 799,
  },
};

export const ReviewsStructuredData = ({
  url,
  urlPath,
  reviews,
  reviewsAggregations,
  priceAggregation,
  localityName,
}: TReviewsStructuredDataProps) => {
  const structuredData = (() => {
    const localityData = localitiesData[urlPath];

    const baseStructuredData = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: `${localityName} RV rentals`,
      description: 'Motorhome rentals, camper van rentals and more.',
      brand: { '@type': 'Brand', name: 'Outdoorsy' },
      review: reviews.map(review => ({
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.score,
          bestRating: 5,
          worstRating: 1,
        },
        author: {
          '@type': 'Person',
          name: `${review.reviewer.first_name} ${review.reviewer.last_name.charAt(0)}.`,
        },
        reviewBody: review.text,
        dateCreated: review.timestamp,
        datePublished: review.timestamp,
      })),
      aggregateRating: reviewsAggregations
        ? {
            '@type': 'AggregateRating',
            ratingValue: reviewsAggregations.avg_score,
            reviewCount: reviewsAggregations.reviews_count,
            bestRating: reviewsAggregations.max_score,
            worstRating: reviewsAggregations.min_score,
          }
        : {
            '@type': 'AggregateRating',
            ratingValue: 4,
            reviewCount: 25791,
            bestRating: 5,
            worstRating: 1,
          },
      ...(priceAggregation && {
        offers: {
          '@type': 'AggregateOffer',
          url,
          priceCurrency: 'USD',
          lowPrice: priceAggregation.min_price / 100,
          highPrice: priceAggregation.max_price / 100,
          offerCount: priceAggregation.rental_count,
        },
      }),
      ...(!priceAggregation &&
        localityData &&
        localityData.type === ECohortType.PricingCohort && {
          offers: {
            '@type': 'AggregateOffer',
            url,
            priceCurrency: 'USD',
            lowPrice: localityData.min_nightly_rate,
            highPrice: localityData.max_nightly_rate,
            offerCount: localityData.num_rentals,
          },
        }),
    };

    return baseStructuredData;
  })();

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData),
        }}
      />
    </Head>
  );
};
